import React, { useEffect } from 'react';
import { Grid, Box, Typography, styled, Avatar, Skeleton } from '@mui/material';
import { CrossIcon } from '../../../assets/';
import { PhysicianProfile,
    BroadAffinity,
    ChannelAffinity,
    VendorAffinity,
    ContentAffinity,
    RxPotential,
    PFIQScore } from '../../../assets/physician-profile';
import { getChannelsPartners, ordinalSuffix, replaceKeyWithValue } from '../../../utility';
import { partnersLogo } from '../../../assets/partners';
import { getHcpProfileData } from '../../../redux/thunks/physiciansThunk';
import { useDispatch, useSelector } from 'react-redux';
import { S3_BUCKET_OBJECT_URL } from '../../../constants';

const PhysiciansProfile = ({
    npiId,
    handleClose
}) => {

    const dispatch = useDispatch();
    const { company } = useSelector(state => state.company);
    const { hcpProfileData, isHcpProfileLoading } = useSelector(state => state.physician);


    const renderTextWithOrdinalSuffix = (arr=[]) => {
        if(arr.length){
            return (
                <Box className="flex-row-start-center flex-wrap gap-4 height-100">
                    {arr?.map((item, index) => {
                        const channel = getChannelsPartners(Object.values(item)[0]?.channel, 'channel');

                        return (
                            <>
                                <Box className="flex-row-center br-50 wh-12px padding-tb-2-rl-2"
                                    sx={{
                                        background: `${
                                            index == 0
                                                ? '#ffcccb'
                                                : index == 1
                                                    ? '#90EE90'
                                                    : '#FFFF00'
                                        }`
                                    }}
                                />
                                <AffinitySubHeading>{channel}</AffinitySubHeading>
                            </>
                        );
                    })}
                </Box>
            );
        };

        return (
            <Box className="flex-row-start-center gap-4 height-100">
                <AffinitySubHeading>-</AffinitySubHeading>
            </Box>
        );

    };


    const renderTextWithOrdinalSuffixObj = (obj = {}) => {
        if (Object.keys(obj).length) {
            const arr = Object?.values(obj || {});
            return (
                <Box className="flex-row-start-center gap-4">
                    {arr?.map((item, index) => {
                        const channel = getChannelsPartners(item.channel, 'channel');
                        return (
                            <>
                                <AffinitySubHeading>{channel}</AffinitySubHeading>
                                <Box
                                    className="flex-row-center br-50 wh-12px padding-tb-2-rl-2"
                                    sx={{
                                        background: `${
                                            index === 0 ? '#ffcccb' : index === 1 ? '#90EE90' : '#FFFF00'
                                        }`
                                    }}
                                />
                            </>
                        );
                    })}
                </Box>
            );
        }

        return (
            <Box className="flex-row-start-center gap-4">
                <AffinitySubHeading>-</AffinitySubHeading>
            </Box>
        );
    };

    const partnersIconMapping = (name) => {
        const brand = name?.toLowerCase();
        return partnersLogo[brand];
    };

    const renderVendorAffinityLogoWithBrand = (value) => (
        <Box className="d-flex gap-2">
            <Avatar
                alt={value}
                className="border-1-gray br-16px wh-15px"
                src={partnersIconMapping(value)}
            >
                {value?.charAt(0)}
            </Avatar>
            <AffinitySubHeading>{value}</AffinitySubHeading>
        </Box>
    );

    const renderPreferencesWithColor = () => (
        <Box className="d-flex gap-4 pt-10px">
            {[1, 2, 3].map((item, index) => (
                <Box className="flex-row-center">
                    <Box
                        sx={{
                            borderRadius: '50%',
                            height: '10px',
                            width: '10px',
                            background: `${
                                index == 0
                                    ? '#ffcccb'
                                    : index == 1
                                        ? '#90EE90'
                                        : '#FFFF00'
                            }`,

                            padding: '4px 2px'
                        }}
                    />
                    <AffinityPreference> {item +ordinalSuffix(item) + ' Preference'} </AffinityPreference>
                </Box>
            ))}
        </Box>
    );

    useEffect(() => {
        if(company.id){
            dispatch(getHcpProfileData({ companyId: company.id, npiId: npiId }));
        }
    },[company.id, dispatch, npiId]);

    return (
        <Box className="custom-modal-inner-container-center bg-none">
            {/* Modal Header */}
            <Grid container className="padding-tb-24-rl-40 bg-primary-500 sticky_header">
                <Grid item xs={12}>
                    <Box className="flex-row-space-between-center pb-16px">
                        <Box>
                            <Typography className="text-lg-medium white">
                                Physician Profile
                            </Typography>
                        </Box>
                        <Box className="padding-10px cursor-pointer" onClick={handleClose}>
                            <img
                                alt="Close the Modal"
                                height="12px"
                                src={CrossIcon}
                                width="12px"
                            />
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    {/* Physicians  Profile */}
                    <Grid container>
                        {/* Physicians Profile Pic, Name and NPI ID*/}
                        <Grid item xs={6}>
                            <Box className="flex-row-align-items-center">
                                <Box className="pr-24px">
                                    <Avatar
                                        alt={hcpProfileData?.personal_details?.name}
                                        className="physician-profile-image"
                                        src={`https://pfiq-v1-dev.s3.us-west-2.amazonaws.com/Rx_images/${hcpProfileData?.npi_id}.jpg`}
                                    />
                                </Box>
                                <Box className="flex-column-start-center">
                                    <Box>
                                        <Typography className="display-xs-medium white">
                                            {isHcpProfileLoading ? <Skeleton height={40} variant='text' width={150} /> :
                                                hcpProfileData?.personal_details?.name
                                            }
                                        </Typography>
                                    </Box>
                                    <Box className="flex-row-align-items-center gap-16">
                                        <AffinitySubHeading
                                            color="#FFFFFF"
                                            fontWeight="500"
                                        >
                                            NPI ID:
                                        </AffinitySubHeading>
                                        <AffinityHeading
                                            color="#FFFFFF"
                                            fontWeight="600"
                                        >
                                            {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={60} /> :
                                                hcpProfileData?.npi_id || '-'
                                            }
                                        </AffinityHeading>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Pysicians Profession Details */}
                        <Grid item xs={6}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Box>
                                        {/* Gender */}
                                        <Box className="pb-16px">
                                            <Typography className="text-xs-medium white">
                                                Gender
                                            </Typography>
                                            <Typography className='text-md-semi-bold white'>
                                                {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                    hcpProfileData?.personal_details?.gender || '-'
                                                }
                                            </Typography>
                                        </Box>

                                        {/* Years of Experience */}
                                        <Box>
                                            <Typography className="text-xs-medium white">
                                                Years of experience:
                                            </Typography>
                                            <Typography className='text-md-semi-bold white'>
                                                {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={60} /> :
                                                    hcpProfileData?.personal_details?.year_of_experience || '-'
                                                }
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item className="border-left-white" xs={6}>
                                    <Box className="padding-tb-0-rl-32">
                                        {/* Speciality */}
                                        <Box className="pb-16px">
                                            <Typography className="text-xs-medium white">
                                                Specialty
                                            </Typography>
                                            <Typography className="text-md-semi-bold white">
                                                {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={80} /> :
                                                    hcpProfileData?.personal_details?.speciality || '-'
                                                }
                                            </Typography>
                                        </Box>

                                        {/* Practice Location */}
                                        <Box>
                                            <Typography className="text-xs-medium white">
                                                Practice Location:
                                            </Typography>
                                            <Typography className="text-md-semi-bold white">
                                                {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                    hcpProfileData?.personal_details?.location?.toUpperCase() || '-'
                                                }
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {/* Modal Body */}
            <Grid container className="physician-profile-modal-body">
                <Grid item xs={12}>
                    <Grid container spacing="8px">
                        {/* Broad Affinity */}
                        <Grid item xs={6}>
                            <AffinityBox className='height-100'>
                                <AffinityImageTitleBox>
                                    <AffinityImage background="#FFF6ED">
                                        <img
                                            alt="Broad Affinity"
                                            height="40px"
                                            src={BroadAffinity}
                                            width="40px"
                                        />
                                    </AffinityImage>
                                    <Box>
                                        <AffinityHeading color="#FB6514">
                                            Broad Affinity
                                        </AffinityHeading>
                                    </Box>
                                </AffinityImageTitleBox>
                                {/*  <Box className="d-flex"> */}
                                {
                                    isHcpProfileLoading ? <Skeleton height={100} variant="rectangular" width="100%" /> :
                                        <Box className="d-flex">
                                            <AffinityBulletsBox>
                                                {hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData?.broad_affinity && Object.keys(hcpProfileData.broad_affinity).length > 0 ? Object.entries(hcpProfileData?.broad_affinity || {})?.map(([preference, affinity], index) => (
                                                    <AffinitySubHeading className='flex-row-start-center flex-wrap gap-4' key={index}>
                                                        <Box className="flex-row-center br-50 wh-12px padding-tb-2-rl-2"
                                                            sx={{
                                                                background: `${index == 0
                                                                    ? '#ffcccb'
                                                                    : index == 1
                                                                        ? '#90EE90'
                                                                        : '#FFFF00'
                                                                }`
                                                            }}
                                                        />
                                                        {replaceKeyWithValue(Object.keys(affinity)[0] || {})}
                                                    </AffinitySubHeading>
                                                )) : '-'}
                                            </AffinityBulletsBox>
                                            <Box className="flex-column gap-8">
                                                {hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData.broad_affinity && Object.keys(hcpProfileData.broad_affinity).length > 0 ? Object.entries(hcpProfileData?.broad_affinity || {})?.map(([preference, affinity], index) => (
                                                    <AffinitySubHeading key={index}>
                                                        {Object.values(affinity)[0] || {}}
                                                    </AffinitySubHeading>
                                                )) : '-'}
                                            </Box>
                                        </Box>
                                }
                                {renderPreferencesWithColor()}
                            </AffinityBox>
                        </Grid>

                        {/* Channel Affinity */}
                        <Grid item xs={6}>
                            <AffinityBox className='height-100'>
                                <AffinityImageTitleBox>
                                    <AffinityImage background="#F0F9FF">
                                        <img
                                            alt="Channel Affinity"
                                            height="40px"
                                            src={ChannelAffinity}
                                            width="40px"
                                        />
                                    </AffinityImage>
                                    <Box>
                                        <AffinityHeading color="#0BA5EC">
                                            Channel Affinity
                                        </AffinityHeading>
                                    </Box>
                                </AffinityImageTitleBox>
                                <Box className="d-flex">
                                    <AffinityBulletsBox>
                                        <AffinitySubHeading>
                                            Digital Affinity
                                        </AffinitySubHeading>
                                        <AffinitySubHeading>
                                            Personal Promotion
                                        </AffinitySubHeading>
                                        <AffinitySubHeading>
                                            Peer To Peer
                                        </AffinitySubHeading>
                                    </AffinityBulletsBox>
                                    <Box className="flex-column gap-8">
                                        {isHcpProfileLoading ? <Skeleton height={20} variant="text" width={200} /> :
                                            renderTextWithOrdinalSuffix(hcpProfileData?.digital_channel_affinity || [])
                                        }
                                        {isHcpProfileLoading ? <Skeleton height={20} variant="text" width={200} /> :
                                            renderTextWithOrdinalSuffixObj(hcpProfileData?.personal_promotion_channel_affinity || {})
                                        }
                                        {isHcpProfileLoading ? <Skeleton height={20} variant="text" width={200} /> :
                                            renderTextWithOrdinalSuffixObj(hcpProfileData?.p2p_channel_affinity || {})
                                        }
                                    </Box>
                                </Box>
                                {renderPreferencesWithColor()}
                            </AffinityBox>
                        </Grid>

                        {/* Vendor Affinity */}
                        <Grid item xs={6}>
                            <AffinityBox className='height-100'>
                                <AffinityImageTitleBox>
                                    <AffinityImage background="#ECFDF3">
                                        <img
                                            alt="Vendor Affinity"
                                            height="40px"
                                            src={VendorAffinity}
                                            width="40px"
                                        />
                                    </AffinityImage>
                                    <Box>
                                        <AffinityHeading color="#12B76A">
                                            Vendor Affinity
                                        </AffinityHeading>
                                    </Box>
                                </AffinityImageTitleBox>
                                {
                                    isHcpProfileLoading ? <Skeleton height={100} variant="rectangular" width="100%" /> :
                                        <Box className="d-flex">
                                            <AffinityBulletsBox>
                                                <AffinitySubHeading>
                                                    {hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData?.digital_channel_affinity && Object.keys(hcpProfileData?.digital_channel_affinity).length > 0
                                                        ? getChannelsPartners(hcpProfileData?.digital_channel_affinity[0]['first_preference']?.channel, 'channel') || '-'
                                                        : '-'
                                                    }
                                                </AffinitySubHeading>
                                                <AffinitySubHeading>
                                                    {hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData?.digital_channel_affinity && Object.keys(hcpProfileData?.digital_channel_affinity).length > 0
                                                        ? getChannelsPartners(hcpProfileData?.digital_channel_affinity[1]['second_preference']?.channel, 'channel') || '-'
                                                        : '-'
                                                    }
                                                </AffinitySubHeading>
                                                <AffinitySubHeading>
                                                    {hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData?.digital_channel_affinity && Object.keys(hcpProfileData?.digital_channel_affinity).length > 0
                                                        ? getChannelsPartners(hcpProfileData?.digital_channel_affinity[2]['third_preference']?.channel, 'channel') || '-'
                                                        : '-'
                                                    }
                                                </AffinitySubHeading>
                                            </AffinityBulletsBox>
                                            <Box className="flex-column gap-8">
                                                {hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData?.digital_channel_affinity && Object.keys(hcpProfileData?.digital_channel_affinity).length > 0 ? hcpProfileData?.digital_channel_affinity?.map((preferenceObj, index) => (
                                                    <Box className="flex-row-start-center flex-wrap gap-4" key={index}>
                                                        {Object.values(preferenceObj || {})?.map((preference) => (
                                                            preference?.vendor && (
                                                                Object.values(preference.vendor || {})?.map((vendorObj) => (
                                                                    Object.keys(vendorObj || {})?.map((vendorName) => (
                                                                        renderVendorAffinityLogoWithBrand(vendorName || '')
                                                                    ))
                                                                ))
                                                            )
                                                        ))}
                                                    </Box>
                                                )) : '-'}
                                            </Box>
                                        </Box>
                                }
                            </AffinityBox>
                        </Grid>

                        {/* Content Affinity */}
                        <Grid item xs={6}>
                            <AffinityBox className='height-100'>
                                <AffinityImageTitleBox>
                                    <AffinityImage background="#F4F3FF">
                                        <img
                                            alt="Content Affinity"
                                            height="40px"
                                            src={ContentAffinity}
                                            width="40px"
                                        />
                                    </AffinityImage>
                                    <Box>
                                        <AffinityHeading color="#7A5AF8">
                                            Content Affinity
                                        </AffinityHeading>
                                    </Box>
                                </AffinityImageTitleBox>
                                <Box className="d-flex">
                                    <AffinityBulletsBox>
                                        <AffinitySubHeading>
                                            Type
                                        </AffinitySubHeading>
                                        <AffinitySubHeading>
                                            Topic
                                        </AffinitySubHeading>
                                        <AffinitySubHeading>
                                            Keywords
                                        </AffinitySubHeading>
                                    </AffinityBulletsBox>
                                    <Box className="flex-column gap-8">
                                        <AffinitySubHeading>
                                            {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                hcpProfileData?.content_affinity?.content_type || '-'
                                            }
                                        </AffinitySubHeading>
                                        <AffinitySubHeading>
                                            {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                hcpProfileData && Object.keys(hcpProfileData).length > 0 && hcpProfileData?.content_affinity && Object.keys(hcpProfileData?.content_affinity).length > 0 ? Object.keys(hcpProfileData?.content_affinity?.topic || {})?.map((preference) => (
                                                    Object.keys(hcpProfileData?.content_affinity?.topic[preference] || {})
                                                ))?.join(', ') : '-'
                                            }
                                        </AffinitySubHeading>
                                        <AffinitySubHeading>
                                            {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                hcpProfileData?.top_three_keywords?.join(', ')
                                            }
                                        </AffinitySubHeading>
                                    </Box>
                                </Box>
                            </AffinityBox>
                        </Grid>

                        {/* Rx Potential */}
                        <Grid item xs={6}>
                            <AffinityBox className='height-100'>
                                <AffinityImageTitleBox>
                                    <AffinityImage background="#EEF4FF">
                                        <img
                                            alt="Rx Potential"
                                            height="40px"
                                            src={RxPotential}
                                            width="40px"
                                        />
                                    </AffinityImage>
                                    <Box>
                                        <AffinityHeading color="#6172F3">
                                            Rx Potential
                                        </AffinityHeading>
                                        <AffinitySubHeading fontWeight="500">
                                            {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                hcpProfileData?.rx_potential || '-'
                                            }
                                        </AffinitySubHeading>
                                    </Box>
                                </AffinityImageTitleBox>
                            </AffinityBox>
                        </Grid>

                        {/* PFIQ Score */}
                        <Grid item xs={6}>
                            <AffinityBox className='height-100'>
                                <AffinityImageTitleBox>
                                    <AffinityImage background="#FAEDF9">
                                        <img
                                            alt="PFIQ Score"
                                            height="40px"
                                            src={PFIQScore}
                                            width="40px"
                                        />
                                    </AffinityImage>
                                    <Box>
                                        <AffinityHeading color="#AD509E">
                                            PFIQ Score
                                        </AffinityHeading>
                                        <AffinitySubHeading fontWeight="500">
                                            {isHcpProfileLoading ? <Skeleton height={20} variant='text' width={50} /> :
                                                hcpProfileData?.pfiq_score || '0'
                                            }
                                        </AffinitySubHeading>
                                    </Box>
                                </AffinityImageTitleBox>
                            </AffinityBox>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

const AffinityBox = styled(Box)({
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '16px'
});

const AffinitySubHeading = styled(Typography)((props) => ({
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: props.fontWeight || '400',
    color: props.color || '#1D2939'
}));

const AffinityHeading = styled(Typography)((props) => ({
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: props.fontWeight || '500',
    color: props.color || '#1D2939'
}));

const AffinityImage = styled(Box)((props) => ({
    borderRadius: '16px',
    padding: '8px',
    background: props.background || '#FFF6ED',
    marginRight: '16px'
}));

const AffinityImageTitleBox = styled(Box)((props) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: '16px'
}));

const AffinityBulletsBox = styled(Box)((props) => ({
    width: 'max-content',
    paddingRight: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
}));

const AffinityPreference = styled(Typography)((props) => ({
    fontSize: '10px',
    lineHeight: '14px',
    fontWeight: '400',
    color: '#1D2939'
}));

export default PhysiciansProfile;
